/**
 * RegistroModals.js — modales de Ver y Editar de un Registro de Ingreso/Gasto.
 *
 * "Ver" reutiliza AccountingDetailModal, el mismo diseño del detalle de Recibo
 * y Factura de CxP (Información General + Detalle Contable con DEBE/HABER,
 * resumen y check de balance), en vez de la tabla cruda del asiento.
 *
 * "Editar" cambia el monto, la fecha, el detalle y las cuentas. Guarda con un
 * PATCH del asiento reconstruyendo sus dos líneas; el backend se encarga de
 * propagar el cambio al movimiento bancario y al saldo de la cuenta (ver
 * AsientoContableViewSet.partial_update).
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { overlayClose } from "../../../../shared/utils/overlayClose";
import AccountingDetailModal from "../../shared/AccountingDetailModal";
import { CuentaSelectSearch } from "../../catalogo/components/CuentaSelect";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import DecimalInput from "../../../../shared/components/DecimalInput";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/** Separa "Detalle | Ref: XYZ" en sus dos partes. */
export function partirConcepto(concepto) {
  const txt = concepto || "";
  const i = txt.lastIndexOf(" | Ref: ");
  if (i === -1) return { detalle: txt, referencia: "" };
  return { detalle: txt.slice(0, i), referencia: txt.slice(i + 8) };
}

/** Monto de un registro en su moneda nativa + el aproximado en la otra. */
export function MontoRegistro({ asiento, color }) {
  const esUSD = (asiento.moneda || "CRC").toUpperCase() === "USD";
  const crc = Number(asiento.total_debitos || 0);
  const usd = Number(asiento.total_debitos_usd || 0);
  if (esUSD) {
    return (
      <div style={{ textAlign: "right" }}>
        <div style={{ fontFamily: "monospace", fontWeight: 600, color }}>
          ${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        {crc > 0 && (
          <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
            ≈ ₡{formatCurrency(crc)}
          </div>
        )}
      </div>
    );
  }
  return (
    <div style={{ textAlign: "right" }}>
      <div style={{ fontFamily: "monospace", fontWeight: 600, color }}>
        ₡{formatCurrency(crc)}
      </div>
      {usd > 0 && (
        <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
          ≈ ${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}
    </div>
  );
}

// ── Ver ───────────────────────────────────────────────────────────────────────

export function VerRegistroModal({ asientoId, tipo, onClose, onEdit }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiFetch(`/api/finance/asientos/${asientoId}/`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData(null));
  }, [asientoId]);

  if (!data) return null;

  const esGasto = tipo === "GASTO";
  const esUSD   = (data.moneda || "CRC").toUpperCase() === "USD";
  const lineas  = data.lineas ?? [];
  // En un GASTO el banco va al HABER (sale plata) y el gasto al DEBE.
  // En un INGRESO es al revés.
  const lineaDebe  = lineas.find((l) => Number(l.debito) > 0)  ?? {};
  const lineaHaber = lineas.find((l) => Number(l.credito) > 0) ?? {};
  const crc = Number(data.total_debitos || 0);
  const usd = lineas.reduce((s, l) => s + Number(l.debito_usd || 0), 0);
  const montoFmt = esUSD
    ? `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `₡${formatCurrency(crc)}`;
  const aprox = esUSD
    ? (crc > 0 ? `≈ ₡${formatCurrency(crc)}` : null)
    : (usd > 0 ? `≈ $${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null);

  // En un GASTO el DEBE es la cuenta de gasto y el HABER el banco (sale plata);
  // en un INGRESO es al revés (la plata entra al banco).
  const { detalle, referencia } = partirConcepto(data.concepto);
  const nombreCuenta = (l) =>
    l.cuenta_nombre ? `${l.cuenta_codigo ?? ""} ${l.cuenta_nombre}`.trim() : "-";

  const infoRows = [
    [t("registros.detalle"), detalle || "-"],
    [t("common.referencia"), referencia || "-"],
    [t("common.fecha"), fmtDate(data.fecha)],
    [t("asientos.numAsiento"), data.numero],
    [esGasto ? t("registros.cuentaBancoLabel") : t("registros.cuentaBancoLabel"),
     nombreCuenta(esGasto ? lineaHaber : lineaDebe)],
    [esGasto ? t("registros.cuentaGastoLabel") : t("registros.cuentaIngresoLabel"),
     nombreCuenta(esGasto ? lineaDebe : lineaHaber)],
    [t("common.monto"), aprox ? `${montoFmt}  (${aprox})` : montoFmt, true],
  ];

  return (
    <AccountingDetailModal
      onClose={onClose}
      onEdit={onEdit}
      icon={esGasto ? "fa-arrow-trend-down" : "fa-arrow-trend-up"}
      iconColor={esGasto ? "#fb923c" : "#4ade80"}
      iconBg={esGasto ? "rgba(251,146,60,0.15)" : "rgba(74,222,128,0.15)"}
      title={`${esGasto ? t("registros.detallesGasto") : t("registros.detallesIngreso")} ${data.numero}`}
      badge={esUSD ? "USD" : null}
      subtitle={`${t("common.fecha")}: ${fmtDate(data.fecha)}`}
      infoTitle={t("cxp.informacionGeneral")}
      infoIcon="fa-circle-info"
      infoRows={infoRows}
      contableTitle={t("cxp.detalleContable")}
      contableIcon="fa-scale-balanced"
      debeLabel={`${esGasto ? t("registros.cuentaGastoLabel") : t("registros.cuentaBancoLabel")} - ${t("asientos.debe").toUpperCase()}`}
      debeCuenta={nombreCuenta(lineaDebe)}
      haberLabel={`${esGasto ? t("registros.cuentaBancoLabel") : t("registros.cuentaIngresoLabel")} - ${t("asientos.haber").toUpperCase()}`}
      haberCuenta={nombreCuenta(lineaHaber)}
      montoFmt={montoFmt}
    />
  );
}

// ── Editar ────────────────────────────────────────────────────────────────────

const OVERLAY = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)", zIndex: 1000,
};
const MODAL = {
  position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
  background: "var(--bg-card)", border: "1px solid var(--border-card)",
  borderRadius: 12, padding: 24, width: "min(560px,94vw)", zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)", maxHeight: "90vh", overflowY: "auto",
};
const LABEL = {
  display: "block", fontSize: "0.78rem", fontWeight: 600,
  color: "var(--text-secondary)", marginBottom: 5,
};
const INP = {
  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-input)",
  borderRadius: 8, padding: "9px 12px", color: "var(--text-primary)", fontSize: "0.9rem",
  outline: "none", boxSizing: "border-box",
};

export function EditarRegistroModal({ asientoId, tipo, onClose, onSaved }) {
  const { t } = useTranslation();
  const multimoneda = useMultimoneda();
  const esGasto = tipo === "GASTO";

  const [data,    setData]    = useState(null);
  const [bancos,  setBancos]  = useState([]);
  const [form,    setForm]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/")
      .then((r) => r.json())
      .then((d) => setBancos((Array.isArray(d) ? d : d.results ?? []).map((b) => ({
        cuentaId: b.cuenta_contable,
        nombre:   `${b.banco} - ${b.numero_cuenta}`,
        moneda:   (b.moneda || "CRC").toUpperCase(),
      }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    apiFetch(`/api/finance/asientos/${asientoId}/`)
      .then((r) => r.json())
      .then((d) => {
        setData(d);
        const lineas     = d.lineas ?? [];
        const lineaDebe  = lineas.find((l) => Number(l.debito) > 0)  ?? {};
        const lineaHaber = lineas.find((l) => Number(l.credito) > 0) ?? {};
        // GASTO: banco = HABER, contrapartida = DEBE. INGRESO: al revés.
        const lBanco  = esGasto ? lineaHaber : lineaDebe;
        const lContra = esGasto ? lineaDebe  : lineaHaber;
        const esUSD   = (d.moneda || "CRC").toUpperCase() === "USD";
        const crc     = Number(d.total_debitos || 0);
        const usd     = lineas.reduce((s, l) => s + Number(l.debito_usd || 0), 0);
        const tcLinea = Number(lBanco.tipo_cambio) || (usd > 0 ? r2(crc / usd) : 1);
        const { detalle, referencia } = partirConcepto(d.concepto);
        setForm({
          fecha:       d.fecha,
          detalle,
          referencia,
          monto:       String(esUSD ? usd : crc),
          moneda:      esUSD ? "USD" : "CRC",
          tipo_cambio: tcLinea > 1 ? tcLinea : 1,
          cuentaBanco: lBanco.cuenta ?? null,
          cuentaContra: lContra.cuenta ?? null,
          cuentaContraNombre: lContra.cuenta_nombre ?? "",
          descBanco:   lBanco.descripcion ?? "",
          descContra:  lContra.descripcion ?? "",
        });
      })
      .catch(() => setError("No se pudo cargar el registro."));
  }, [asientoId, esGasto]);

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const montoNum = Number(form.monto) || 0;
  const esUSD    = form.moneda === "USD";
  const tcNum    = esUSD ? (Number(form.tipo_cambio) || 1) : 1;
  const montoCRC = esUSD ? r2(montoNum * tcNum) : montoNum;
  const montoUSD = esUSD ? montoNum : 0;

  async function handleSave() {
    if (!form.fecha)            { setError("La fecha es obligatoria."); return; }
    if (!form.detalle.trim())   { setError("El detalle es obligatorio."); return; }
    if (montoNum <= 0)          { setError("El monto debe ser mayor a 0."); return; }
    if (!form.cuentaBanco)      { setError("Seleccioná la cuenta de banco."); return; }
    if (!form.cuentaContra)     { setError(esGasto ? "Seleccioná la cuenta de gasto." : "Seleccioná la cuenta de ingreso."); return; }
    if (esUSD && tcNum <= 1)    { setError("Ingresá un tipo de cambio válido."); return; }

    setSaving(true); setError("");
    try {
      const concepto = form.detalle.trim() +
        (form.referencia.trim() ? ` | Ref: ${form.referencia.trim()}` : "");

      const lineaContra = {
        cuenta: form.cuentaContra,
        debito:  esGasto ? montoCRC : 0,
        credito: esGasto ? 0 : montoCRC,
        debito_usd:  esGasto ? montoUSD : 0,
        credito_usd: esGasto ? 0 : montoUSD,
        moneda: form.moneda, tipo_cambio: tcNum,
        descripcion: form.descContra || "",
      };
      const lineaBanco = {
        cuenta: form.cuentaBanco,
        debito:  esGasto ? 0 : montoCRC,
        credito: esGasto ? montoCRC : 0,
        debito_usd:  esGasto ? 0 : montoUSD,
        credito_usd: esGasto ? montoUSD : 0,
        moneda: form.moneda, tipo_cambio: tcNum,
        descripcion: form.descBanco || "",
      };

      const res = await apiFetch(`/api/finance/asientos/${asientoId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          fecha: form.fecha,
          concepto,
          // El orden DEBE → HABER mantiene la lectura del asiento igual que al crearlo
          lineas: esGasto ? [lineaContra, lineaBanco] : [lineaBanco, lineaContra],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Error al actualizar el registro.");
      }
      onSaved?.(esGasto ? "Gasto actualizado correctamente." : "Ingreso actualizado correctamente.");
      onClose();
    } catch (ex) { setError(ex.message); }
    finally { setSaving(false); }
  }

  const field = (label, children) => (
    <div><label style={LABEL}>{label}</label>{children}</div>
  );

  return (
    <>
      <div {...overlayClose(onClose)} style={OVERLAY}/>
      <div style={MODAL}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--border-card)" }}>
          <div>
            <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
              <i className="fa-solid fa-square-pen" style={{ color: "#f97316", marginRight: 8 }}/>
              {esGasto ? "Editar Gasto" : "Editar Ingreso"}
            </h3>
            <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "0.8rem" }}>
              {data?.numero}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: "var(--text-muted)", cursor: "pointer", fontSize: "1.2rem" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        {/* Aviso: la edición toca banco + asiento + movimiento */}
        <div style={{ display: "flex", gap: 9, alignItems: "flex-start",
          background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)",
          borderRadius: 8, padding: "9px 12px", marginBottom: 14 }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ color: "#fbbf24", marginTop: 2 }}/>
          <span style={{ color: "var(--text-secondary)", fontSize: "0.78rem", lineHeight: 1.5 }}>
            Cambiar el monto o la cuenta actualiza también el asiento de diario, el
            movimiento bancario y el saldo de la cuenta.
          </span>
        </div>

        {error && (
          <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)",
            borderRadius: 8, padding: "9px 12px", marginBottom: 12, color: "#f87171", fontSize: "0.83rem" }}>
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 7 }}/>{error}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          {field(`${t("common.fecha")} *`,
            <input type="date" style={INP} value={form.fecha}
              onChange={(e) => set("fecha", e.target.value)}/>
          )}
          {field(t("common.referencia"),
            <input type="text" style={INP} value={form.referencia}
              onChange={(e) => set("referencia", e.target.value)}
              placeholder={t("registros.referenciaPlaceholder")}/>
          )}

          <div style={{ gridColumn: "span 2" }}>
            {field(`${t("registros.detalle")} *`,
              <input type="text" style={INP} value={form.detalle}
                onChange={(e) => set("detalle", e.target.value)}/>
            )}
          </div>

          {field(`${t("common.monto")} *`,
            <input type="number" min="0.01" step="0.01" style={INP} value={form.monto}
              onChange={(e) => set("monto", e.target.value)}/>
          )}
          {multimoneda ? field("Moneda",
            <select style={{ ...INP, cursor: "pointer" }} value={form.moneda}
              onChange={(e) => set("moneda", e.target.value)}>
              <option value="CRC">₡ Colones</option>
              <option value="USD">$ Dólares</option>
            </select>
          ) : <div/>}

          {multimoneda && esUSD && (
            <div style={{ gridColumn: "span 2" }}>
              {field("Tipo de cambio",
                <DecimalInput style={INP} value={form.tipo_cambio}
                  onChange={(e) => set("tipo_cambio", e.target.value)}/>
              )}
              <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 4 }}>
                ≈ ₡{formatCurrency(montoCRC)}
              </div>
            </div>
          )}

          <div style={{ gridColumn: "span 2" }}>
            {field(`${t("registros.cuentaBancoLabel")} *`,
              <select style={{ ...INP, cursor: "pointer" }} value={form.cuentaBanco ?? ""}
                onChange={(e) => {
                  const id = Number(e.target.value) || null;
                  const b  = bancos.find((x) => x.cuentaId === id);
                  setForm((f) => ({ ...f, cuentaBanco: id, ...(b ? { moneda: b.moneda } : {}) }));
                }}>
                <option value="">{t("registros.seleccionaCuentaBanco")}</option>
                {bancos.map((b) => (
                  <option key={b.cuentaId} value={b.cuentaId}>
                    {b.nombre}{b.moneda === "USD" ? " (USD)" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div style={{ gridColumn: "span 2" }}>
            {field(`${esGasto ? t("registros.cuentaGastoLabel") : t("registros.cuentaIngresoLabel")} *`,
              <CuentaSelectSearch
                value={form.cuentaContra}
                onChange={(id, obj) => {
                  set("cuentaContra", id || null);
                  set("cuentaContraNombre", obj?.nombre ?? "");
                }}
                tipo={esGasto ? "GASTO" : "INGRESO"}
                placeholder={esGasto ? t("registros.seleccionaCuentaGasto") : t("registros.seleccionaCuentaIngreso")}
              />
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: "8px 18px", background: "transparent",
            border: "1px solid var(--border-card)", borderRadius: 7, color: "var(--text-primary)",
            fontSize: "0.83rem", fontWeight: 600, cursor: "pointer" }}>
            {t("common.cancelar")}
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "8px 18px",
            background: "#f97316", border: "none", borderRadius: 7, color: "#fff",
            fontSize: "0.83rem", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1, display: "flex", alignItems: "center", gap: 7 }}>
            <i className={`fa-solid ${saving ? "fa-circle-notch fa-spin" : "fa-floppy-disk"}`}/>
            {saving ? t("registros.guardando") : t("cxp.guardarCambios")}
          </button>
        </div>
      </div>
    </>
  );
}
